/**
 * The left rail: the reader's notebooks, and the current notebook's SOURCES.
 *
 * It used to carry a scope tree — every collection, with tri-state tick-boxes, expandable
 * to its documents. That belonged to the conversation model, where the whole corpus was
 * the floor and a reader narrowed down. A notebook is the opposite: it starts empty and is
 * filled deliberately, so the world of things that COULD be added moved into the
 * "Tambah sumber" dialog. With both on screen the notebook disappeared behind its own
 * picker (measured: 50 collections listed above 3 actual sources).
 *
 * What remains here is deliberately small: what you have, and what is in it.
 */
import * as Tabs from "@radix-ui/react-tabs";
import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import type { Conversation } from "@/lib/api-types.ts";
import { Button } from "./primitives.tsx";

export function Rail({
  conversations,
  activeConversationId,
  onNewNotebook,
  sourcesPanel,
  user,
  onSignOut,
}: {
  conversations: Conversation[];
  activeConversationId: string | null;
  onNewNotebook: () => void | Promise<void>;
  /** The notebook's own sources, grouped by collection. Rendered above the corpus-wide
   *  tick-boxes because it answers the first question a reader has — what is IN here —
   *  where the list below answers what could be added. */
  sourcesPanel?: React.ReactNode;
  user: { displayName: string; role: string; subject: string };
  onSignOut: () => void;
}) {
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);
  // CONTROLLED, so opening a notebook lands on its SOURCES rather than on the list of
  // notebooks you just came from. The first question a reader has inside a notebook is
  // what is in it; the list answered a question they had already finished asking.
  const [tab, setTab] = useState(activeConversationId ? "sources" : "notebooks");
  useEffect(() => {
    if (activeConversationId) setTab("sources");
  }, [activeConversationId]);

  return (
    <nav className="rail" aria-label={t("app.name")}>
      <div className="rail-head">
        <Link to="/" className="brand" style={{ textDecoration: "none", color: "inherit" }}>
          <span className="brand-mark" aria-hidden="true">
            PA
          </span>
          <span>
            <span className="brand-name">{t("app.name")}</span>
            <span className="brand-sub">{t("app.prototype")}</span>
          </span>
        </Link>
      </div>

      <div style={{ padding: "0 var(--s2) var(--s2)" }}>
        <Button
          variant="primary"
          // GUARDED, like the same button on the home page: without this a double click
          // created two notebooks and navigated to the second, stranding the first.
          disabled={creating}
          onClick={() => {
            setCreating(true);
            void Promise.resolve(onNewNotebook()).finally(() => setCreating(false));
          }}
          style={{ width: "100%" }}
        >
          {t("nav.newNotebook")}
        </Button>
      </div>

      <Tabs.Root value={tab} onValueChange={setTab} style={{ display: "contents" }}>
        <div style={{ padding: "0 var(--s2) var(--s2)" }}>
          <Tabs.List className="seg" aria-label={t("nav.studio")}>
            <Tabs.Trigger value="notebooks">{t("nav.notebooks")}</Tabs.Trigger>
            <Tabs.Trigger value="sources">{t("nav.sources")}</Tabs.Trigger>
          </Tabs.List>
        </div>

        <div className="rail-scroll">
          <Tabs.Content value="notebooks" className="pane">
            {conversations.length === 0 ? (
              <p className="hint" style={{ padding: "var(--s3) var(--s2)" }}>
                {t("home.noRecent")}
              </p>
            ) : (
              conversations.map((conversation) => (
                <Link
                  key={conversation.id}
                  to="/studio/$conversationId"
                  params={{ conversationId: conversation.id }}
                  className="conv"
                  aria-current={activeConversationId === conversation.id ? "true" : undefined}
                >
                  {conversation.title ?? t("home.untitled")}
                </Link>
              ))
            )}
          </Tabs.Content>

          <Tabs.Content value="sources" className="pane">
            {/* THE NOTEBOOK'S OWN SOURCES, and nothing else.
                This used to list all 50 collections with tick-boxes, permanently. That was
                the conversation model, where the whole corpus was the floor and a reader
                narrowed down. A notebook is the opposite: it starts empty and is filled
                deliberately, so the world of things that COULD be added belongs in the
                "Tambah sumber" dialog, not in the rail. With both on screen the notebook
                disappeared behind its own picker. */}
            {sourcesPanel}
          </Tabs.Content>
        </div>
      </Tabs.Root>

      <div className="rail-foot">
        <span className="avatar" aria-hidden="true">
          {user.displayName.slice(0, 1).toUpperCase()}
        </span>
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{ display: "block", fontSize: 13, fontWeight: 600 }}>
            {user.displayName}
          </span>
          <span className="brand-sub">{user.role}</span>
        </span>
        {/* No sign-out affordance existed on ANY mockup screen. */}
        <Button size="sm" variant="ghost" onClick={onSignOut}>
          {t("nav.signOut")}
        </Button>
      </div>
    </nav>
  );
}

